/**
 * Parse a payroll period boundary (periodStart/periodEnd) into a valid Date.
 *
 * Period values are stored inconsistently across the app: a fresh draft holds
 * a 'YYYY-MM-DD' string, but a run that went through Submit persists a Firestore
 * Timestamp (the wizard holds a Date and setDoc serializes it). Naive parsing
 * (`new Date(value + 'T00:00:00')`) yields an Invalid Date for a Timestamp,
 * which crashes date-fns `format` downstream (e.g. PeriodSelection on resume).
 *
 * Handles Timestamp | Date | 'YYYY-MM-DD' string, and returns null for
 * falsy/invalid input (e.g. a corrupted "Invalid Date" index entry) so callers
 * can fall back instead of throwing.
 *
 * @param {*} v - Firestore Timestamp, Date, or 'YYYY-MM-DD' string
 * @returns {Date|null}
 */
export const parsePeriodToDate = (v) => {
  if (!v) return null;
  let d;
  if (v.toDate) d = v.toDate();                          // Firestore Timestamp
  else if (v instanceof Date) d = v;                     // already a Date
  else d = new Date(`${v}`.slice(0, 10) + 'T00:00:00');  // 'YYYY-MM-DD' string (local midnight)
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Serialize a period boundary to a 'YYYY-MM-DD' string for storage.
 *
 * Use this when WRITING periodStart/periodEnd so runs never persist a Firestore
 * Timestamp (a Date written via setDoc becomes one) — that's the class that
 * caused the DTR-lock bug and the resume crash. Uses LOCAL Y/M/D components,
 * never toISOString (UTC would shift the calendar date at TZ boundaries and
 * change which DTR records fall in the period window).
 *
 * @param {*} v - Firestore Timestamp, Date, or 'YYYY-MM-DD' string
 * @returns {string} 'YYYY-MM-DD', or '' for falsy/invalid input
 */
export const toYMDString = (v) => {
  const d = parsePeriodToDate(v);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/**
 * Format a date boundary for display as 'Feb 23, 2026'.
 *
 * Normalizes through toYMDString FIRST, then formats the string parts — it never
 * constructs a Date from the value, so a Firestore Timestamp renders correctly
 * instead of "Invalid Date". That matters wherever a message *about* a date is
 * shown: `new Date(<Timestamp>)` is Invalid, and the error text explaining a date
 * problem would itself ship the date bug (BL-043's employment-window message).
 *
 * Note: the component-local `formatDateToFilipino` in StepEmployment.js does
 * `new Date(dateString)` and has exactly that flaw; it is not exported and is not
 * a substitute.
 *
 * @param {*} v - Firestore Timestamp, Date, or 'YYYY-MM-DD' string
 * @returns {string} e.g. 'Feb 23, 2026', or '' for falsy/invalid input
 */
export const formatYMDForDisplay = (v) => {
  const ymd = toYMDString(v);
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-');
  return `${MONTHS_SHORT[Number(m) - 1]} ${Number(d)}, ${y}`;
};

/**
 * Today's calendar date in Asia/Manila as 'YYYY-MM-DD'.
 *
 * Sahod is Philippine-only, but a device (or the AU-based founder) can sit in
 * another timezone, so this pins to Manila explicitly rather than trusting the
 * browser's local day. Use it wherever "today" must match the server's Manila
 * day — e.g. resolving the in-force SSS contribution-table version at employee
 * setup (greatest effectiveDate ≤ today). en-CA yields ISO 'YYYY-MM-DD'.
 *
 * @returns {string} 'YYYY-MM-DD' in Asia/Manila
 */
export const todayManilaYMD = () =>
  new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Manila' });

/**
 * The Manila calendar date of an INSTANT (a punch, a stored timeIn/timeOut) —
 * 'YYYY-MM-DD'. Never pass it a calendar-day Date (a local midnight such as a
 * page's selectedDate): from a browser ahead of Manila that midnight is the
 * PREVIOUS Manila day (PRD_TimeRecordAndPayFixes C3 rule).
 *
 * @param {Date} instant
 * @returns {string} 'YYYY-MM-DD' in Asia/Manila
 */
export const manilaYMD = (instant) =>
  instant.toLocaleDateString('en-CA', { timeZone: 'Asia/Manila' });

/**
 * The Manila wall-clock time of an INSTANT as 24h 'HH:MM' (en-GB, like the DTR
 * model's generatedAt stamp).
 *
 * @param {Date} instant
 * @returns {string} 'HH:MM' in Asia/Manila
 */
export const manilaHHMM = (instant) =>
  instant.toLocaleTimeString('en-GB', { timeZone: 'Asia/Manila', hour: '2-digit', minute: '2-digit' });

/**
 * Get all 'YYYY-MM' month strings that fall within a period range (inclusive).
 *
 * Pure string math on 'YYYY-MM-DD' bounds — used to know which monthly DTR
 * documents a payroll period spans (DTR doc id = '{YYYY-MM}_{employeeId}').
 * Uses local Y/M components only; no Date/UTC, so no timezone drift.
 *
 * @param {string} periodStart - 'YYYY-MM-DD'
 * @param {string} periodEnd   - 'YYYY-MM-DD'
 * @returns {string[]} e.g. ['2026-01', '2026-02']
 */
export const getMonthsInRange = (periodStart, periodEnd) => {
  const [startYear, startMonth] = periodStart.split('-').map(Number);
  const [endYear, endMonth] = periodEnd.split('-').map(Number);

  const months = [];
  let year = startYear;
  let month = startMonth;

  while (year < endYear || (year === endYear && month <= endMonth)) {
    months.push(`${year}-${String(month).padStart(2, '0')}`);
    month++;
    if (month > 12) {
      month = 1;
      year++;
    }
  }

  return months;
};
